import React, { useState, useEffect, useCallback } from 'react';
import { 
  Table, 
  Button, 
  Select, 
  message, 
  Tag, 
  Space,
  Card,
  Modal,
  Descriptions,
  Dropdown
} from 'antd';
import { 
  ReloadOutlined,
  EyeOutlined,
  CloseCircleOutlined,
  DownOutlined
} from '@ant-design/icons';
import api from '../utils/api';

const { Option } = Select;

const CommandHistory = () => {
  const [commands, setCommands] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterDevice, setFilterDevice] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [selectedCommand, setSelectedCommand] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [commandsRes, devicesRes] = await Promise.all([
        api.get('/commands/history', {
          params: { deviceId: filterDevice }
        }),
        api.get('/devices')
      ]);

      let commandsData = commandsRes.data.data || [];
      if (filterStatus) {
        commandsData = commandsData.filter(c => c.status === filterStatus);
      }

      setCommands(commandsData);
      setDevices(devicesRes.data.data || []);
    } catch (error) {
      message.error('获取数据失败');
      console.error('Fetch data error:', error);
    } finally {
      setLoading(false);
    }
  }, [filterDevice, filterStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCancel = async (commandId) => {
    try {
      await api.delete(`/commands/${commandId}`);
      message.success('指令已取消');
      fetchData();
    } catch (error) {
      message.error('取消指令失败');
      console.error('Cancel command error:', error);
    }
  };

  const showDetail = (command) => {
    setSelectedCommand(command);
    setDetailVisible(true);
  };

  const columns = [
    {
      title: '指令ID',
      dataIndex: 'commandId',
      key: 'commandId',
      ellipsis: true,
      width: 200
    },
    {
      title: '设备名称',
      dataIndex: 'deviceId',
      key: 'deviceId',
      render: (deviceId) => {
        const device = devices.find(d => d.deviceId === deviceId);
        return device ? device.deviceName : deviceId;
      }
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        const typeMap = {
          install: '安装',
          uninstall: '卸载',
          update: '更新',
          wipe: '擦除',
          lock: '锁定'
        };
        return <Tag color="blue">{typeMap[type] || type}</Tag>;
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colorMap = {
          pending: 'orange',
          in_progress: 'blue',
          success: 'green',
          failed: 'red',
          cancelled: 'gray'
        };
        const labelMap = {
          pending: '待执行',
          in_progress: '执行中',
          success: '成功',
          failed: '失败',
          cancelled: '已取消'
        };
        return <Tag color={colorMap[status]}>{labelMap[status] || status}</Tag>;
      }
    },
    {
      title: '创建者',
      dataIndex: 'createdBy',
      key: 'createdBy'
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleString('zh-CN')
    },
    {
      title: '执行时间',
      dataIndex: 'executedAt',
      key: 'executedAt',
      render: (date) => date ? new Date(date).toLocaleString('zh-CN') : '-'
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'detail',
                icon: <EyeOutlined />,
                label: '详情',
                onClick: () => showDetail(record)
              },
              ...(record.status === 'pending' ? [
                {
                  type: 'divider'
                },
                {
                  key: 'cancel',
                  icon: <CloseCircleOutlined />,
                  label: '取消',
                  danger: true,
                  onClick: () => {
                    Modal.confirm({
                      title: '确定要取消这个指令吗？',
                      content: '取消后将无法恢复',
                      okText: '确定',
                      cancelText: '取消',
                      okButtonProps: { danger: true },
                      onOk: () => handleCancel(record.commandId)
                    });
                  }
                }
              ] : [])
            ]
          }}
          trigger={['hover']}
        >
          <Button>
            操作 <DownOutlined />
          </Button>
        </Dropdown>
      )
    }
  ];

  return (
    <div>
      <Card
        title="指令历史"
        extra={
          <Space>
            <Select
              placeholder="选择设备"
              style={{ width: 200 }}
              allowClear
              onChange={setFilterDevice}
              value={filterDevice}
            >
              {devices.map(device => (
                <Option key={device.deviceId} value={device.deviceId}>
                  {device.deviceName}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="选择状态"
              style={{ width: 150 }}
              allowClear
              onChange={setFilterStatus}
              value={filterStatus}
            >
              <Option value="pending">待执行</Option>
              <Option value="in_progress">执行中</Option>
              <Option value="success">成功</Option>
              <Option value="failed">失败</Option>
              <Option value="cancelled">已取消</Option>
            </Select>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchData}
              loading={loading}
            >
              刷新
            </Button>
          </Space>
        }
      >
        <Table
          dataSource={commands}
          columns={columns}
          rowKey="commandId"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条指令`
          }}
        />
      </Card>

      <Modal
        title="指令详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {selectedCommand && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="指令ID" span={2}>
              {selectedCommand.commandId}
            </Descriptions.Item>
            <Descriptions.Item label="设备ID" span={2}>
              {selectedCommand.deviceId}
            </Descriptions.Item>
            <Descriptions.Item label="类型">
              {selectedCommand.type}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={
                selectedCommand.status === 'success' ? 'green' :
                selectedCommand.status === 'failed' ? 'red' :
                selectedCommand.status === 'pending' ? 'orange' : 'blue'
              }>
                {selectedCommand.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="创建者">
              {selectedCommand.createdBy}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {new Date(selectedCommand.createdAt).toLocaleString('zh-CN')}
            </Descriptions.Item>
            <Descriptions.Item label="执行时间">
              {selectedCommand.executedAt ? 
                new Date(selectedCommand.executedAt).toLocaleString('zh-CN') : 
                '-'}
            </Descriptions.Item>
            <Descriptions.Item label="负载参数" span={2}>
              <pre style={{ 
                background: '#f5f5f5', 
                padding: '8px', 
                borderRadius: '4px',
                margin: 0 
              }}>
                {JSON.stringify(selectedCommand.payload, null, 2)}
              </pre>
            </Descriptions.Item>
            {selectedCommand.result && (
              <Descriptions.Item label="执行结果" span={2}>
                <pre style={{ 
                  background: '#f5f5f5', 
                  padding: '8px', 
                  borderRadius: '4px',
                  margin: 0 
                }}>
                  {JSON.stringify(selectedCommand.result, null, 2)}
                </pre>
              </Descriptions.Item>
            )}
            {selectedCommand.errorMessage && (
              <Descriptions.Item label="错误信息" span={2}>
                <pre style={{ 
                  background: '#fff1f0', 
                  padding: '8px', 
                  borderRadius: '4px',
                  margin: 0,
                  color: '#ff4d4f'
                }}>
                  {selectedCommand.errorMessage}
                </pre>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default CommandHistory;
